import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { LogOut, Upload, Shield, Library, Wallet as WalletIcon, Menu, UserCog } from "lucide-react";
import Logo from "@/components/Logo";
import NavInstallPill from "@/components/NavInstallPill";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Same set of links the desktop tabs render, gated by the same role
  // checks. Keeps the two surfaces in lock-step.
  const navLinks = [
    { to: "/browse", label: "Browse", show: true, testId: "mobile-nav-browse" },
    { to: "/event", label: "Launch", show: true, testId: "mobile-nav-event" },
    { to: "/submit-film", label: "Submit Film", show: true, testId: "mobile-nav-submit" },
    { to: "/blog", label: "Journal", show: true, testId: "mobile-nav-blog" },
    { to: "/about", label: "About", show: true, testId: "mobile-nav-about" },
    { to: "/filmmaker", label: "Filmmaker", show: !!user && user.role === "filmmaker", testId: "mobile-nav-filmmaker" },
    { to: "/admin", label: "Admin", show: !!user && user.role === "admin", testId: "mobile-nav-admin" },
    { to: "/library", label: "My Library", show: !!user, testId: "mobile-nav-library" },
  ].filter((l) => l.show);

  return (
    <nav
      className="glass fixed top-0 left-0 right-0 z-40 px-4 sm:px-8 py-3 flex items-center justify-between"
      data-testid="main-navbar"
    >
      <Link to="/" className="flex items-center group" data-testid="nav-logo">
        <Logo mark size={36} className="transition-transform group-hover:scale-105" />
        <span className="font-display font-black text-lg tracking-tight ml-2.5 hidden sm:inline">
          <span className="text-white">cine</span><span className="text-terracotta">voya</span>
        </span>
      </Link>

      <div className="hidden md:flex items-center gap-8 text-sm">
        <Link to="/browse" className="text-foreground/80 hover:text-foreground transition-colors" data-testid="nav-browse">
          Browse
        </Link>
        <Link to="/event" className="text-terracotta hover:text-ember transition-colors font-semibold" data-testid="nav-event">
          Launch
        </Link>
        <Link to="/submit-film" className="text-foreground/80 hover:text-foreground transition-colors" data-testid="nav-submit">
          Submit Film
        </Link>
        <Link to="/blog" className="text-foreground/80 hover:text-foreground transition-colors" data-testid="nav-blog">
          Journal
        </Link>
        <Link to="/about" className="text-foreground/80 hover:text-foreground transition-colors" data-testid="nav-about">
          About
        </Link>
        {user && user.role === "filmmaker" && (
          <Link to="/filmmaker" className="text-foreground/80 hover:text-foreground transition-colors" data-testid="nav-filmmaker">
            Filmmaker
          </Link>
        )}
        {user && user.role === "admin" && (
          <Link to="/admin" className="text-foreground/80 hover:text-foreground transition-colors" data-testid="nav-admin">
            Admin
          </Link>
        )}
        {user && (
          <Link to="/library" className="text-foreground/80 hover:text-foreground transition-colors" data-testid="nav-library">
            My Library
          </Link>
        )}
      </div>

      {user ? (
        <div className="flex items-center gap-1 sm:gap-2">
          <NavInstallPill />
          {/* Mobile hamburger — trigger is hidden on md+ where the tabs render */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden h-9 w-9"
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-surface border-white/10 text-foreground w-72 p-0" data-testid="mobile-menu-drawer">
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/10 text-left">
                <SheetTitle className="font-display font-black text-xl">
                  <span className="text-white">cine</span><span className="text-terracotta">voya</span>
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col py-3" data-testid="mobile-menu-nav">
                {navLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    className="px-6 py-3 text-base text-foreground/85 hover:bg-white/5 hover:text-foreground transition-colors"
                    data-testid={l.testId}
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>
            </SheetContent>
          </Sheet>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="nav-user-menu">
                <div className="w-7 h-7 rounded-full overflow-hidden bg-terracotta flex items-center justify-center text-white text-sm font-semibold">
                  {user.avatar_path ? (
                    <img
                      src={`${API}/users/${user.id}/avatar${user.avatar_updated_at ? `?v=${encodeURIComponent(user.avatar_updated_at)}` : ""}`}
                      alt={user.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    user.name?.[0]?.toUpperCase() || "U"
                  )}
                </div>
                <span className="hidden sm:inline text-sm">{user.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-surface border-white/10 w-56">
              <DropdownMenuLabel className="text-xs text-muted-foreground capitalize">
                {user.role}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={() => navigate("/library")} data-testid="menu-library" className="cursor-pointer">
                <Library className="w-4 h-4 mr-2" /> My Library
              </DropdownMenuItem>
              {user.role !== "admin" && (
                <DropdownMenuItem onClick={() => navigate("/wallet")} data-testid="menu-wallet" className="cursor-pointer">
                  <WalletIcon className="w-4 h-4 mr-2" /> Wallet
                </DropdownMenuItem>
              )}
              {user.role === "filmmaker" && (
                <>
                  <DropdownMenuItem onClick={() => navigate("/filmmaker/upload")} data-testid="menu-upload" className="cursor-pointer">
                    <Upload className="w-4 h-4 mr-2" /> Upload Film
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/filmmaker/profile")} data-testid="menu-edit-profile" className="cursor-pointer">
                    <UserCog className="w-4 h-4 mr-2" /> Edit Profile
                  </DropdownMenuItem>
                </>
              )}
              {user.role === "admin" && (
                <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="cursor-pointer">
                  <Shield className="w-4 h-4 mr-2" /> Admin Panel
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <div className="flex items-center gap-1 sm:gap-2">
          <NavInstallPill />
          {/* Mobile hamburger for guests too — Browse + About are still useful */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden h-9 w-9"
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-surface border-white/10 text-foreground w-72 p-0" data-testid="mobile-menu-drawer">
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/10 text-left">
                <SheetTitle className="font-display font-black text-xl">
                  <span className="text-white">cine</span><span className="text-terracotta">voya</span>
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col py-3" data-testid="mobile-menu-nav">
                {navLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    className="px-6 py-3 text-base text-foreground/85 hover:bg-white/5 hover:text-foreground transition-colors"
                    data-testid={l.testId}
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>
            </SheetContent>
          </Sheet>

          <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn">
            Login
          </Button>
          <Button
            className="bg-terracotta hover:bg-ember text-white"
            onClick={() => navigate("/register")}
            data-testid="nav-register-btn"
          >
            Get started
          </Button>
        </div>
      )}
    </nav>
  );
}
